import { useEffect, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import CommercialNotice from "../components/CommercialNotice";
import { Plus, X, Pencil } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
} from "@dnd-kit/core";

const COLS = [
  { key: "target", label: "Target" },
  { key: "contacted", label: "Contacted" },
  { key: "replied", label: "Replied" },
  { key: "negotiating", label: "Negotiating" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

function DealCard({ deal, onEdit, dragging }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: deal.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ touchAction: "none", opacity: isDragging ? 0.3 : 1 }}
      className="bg-bg-elev border border-line p-3 cursor-grab active:cursor-grabbing hover:border-white transition-colors select-none"
      data-testid={`deal-card-${deal.id}`}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="font-semibold text-sm leading-tight">{deal.brand?.brand_name || "Brand"}</div>
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(deal); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="text-ink-muted hover:text-white shrink-0"
          data-testid={`edit-deal-${deal.id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="text-xs text-ink-muted mt-1">{deal.brand?.category || ""}</div>
      {deal.estimated_value > 0 && <div className="text-xs text-volt font-mono mt-2">£{Number(deal.estimated_value).toLocaleString()}</div>}
      {deal.next_follow_up_date && <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-1">Follow-up: {deal.next_follow_up_date.slice(0,10)}</div>}
    </div>
  );
}

function DealCardPreview({ deal }) {
  return (
    <div className="bg-bg-elev border border-volt p-3 shadow-2xl cursor-grabbing select-none" style={{ width: 240 }}>
      <div className="font-semibold text-sm leading-tight">{deal.brand?.brand_name || "Brand"}</div>
      <div className="text-xs text-ink-muted mt-1">{deal.brand?.category || ""}</div>
    </div>
  );
}

function KanbanColumn({ col, items, onEdit }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  return (
    <div
      ref={setNodeRef}
      className={`bg-bg-surface border min-w-[260px] lg:min-w-0 flex flex-col transition-colors ${isOver ? "border-volt" : "border-line"}`}
      data-testid={`kanban-col-${col.key}`}
    >
      <div className="px-4 py-3 border-b border-line flex justify-between items-center">
        <div className="label-eyebrow">{col.label}</div>
        <div className="text-xs text-ink-muted font-mono">{items.length}</div>
      </div>
      <div className="p-2 space-y-2 min-h-[200px]">
        {items.map(d => <DealCard key={d.id} deal={d} onEdit={onEdit} />)}
      </div>
    </div>
  );
}

export default function CRM() {
  const [deals, setDeals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const load = () => api.get("/deals").then(({data}) => setDeals(data));
  useEffect(() => { load(); }, []);

  const onDragStart = (e) => setActiveId(e.active.id);
  const onDragEnd = async (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const newStatus = over.id;
    const id = active.id;
    const current = deals.find(d => d.id === id);
    if (!current || current.status === newStatus) return;
    setDeals(ds => ds.map(d => d.id === id ? { ...d, status: newStatus } : d));
    try {
      await api.patch(`/deals/${id}`, { status: newStatus });
    } catch (err) {
      // Roll back optimistic update on failure
      setDeals(ds => ds.map(d => d.id === id ? { ...d, status: current.status } : d));
    }
  };

  const save = async (deal) => {
    await api.patch(`/deals/${deal.id}`, {
      status: deal.status, estimated_value: Number(deal.estimated_value) || 0,
      notes: deal.notes, next_follow_up_date: deal.next_follow_up_date
    });
    setEditing(null);
    load();
  };

  const activeDeal = activeId ? deals.find(d => d.id === activeId) : null;

  // Pipeline KPI chips — gives athletes a one-glance feel for where their value sits.
  const wonTotal = deals
    .filter(d => d.status === "won")
    .reduce((sum, d) => sum + (Number(d.estimated_value) || 0), 0);
  const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const atRisk = deals.filter(d => {
    if (d.status !== "negotiating") return false;
    const ts = d.updated_at || d.created_at;
    if (!ts) return false;
    return now - new Date(ts).getTime() > TWO_WEEKS_MS;
  });
  const inFlight = deals.filter(d => ["contacted", "replied", "negotiating"].includes(d.status)).length;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12">
        <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow">Sponsorship pipeline</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="crm-title">CRM</h1>
            <p className="text-ink-muted mt-2 text-sm">Drag deals across stages. Add brands first from the Brands page.</p>
          </div>
        </div>

        {/* KPI chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="crm-kpi-chips">
          <div className="border border-volt/60 bg-volt/5 px-4 py-3 flex items-center gap-3" data-testid="kpi-won">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-volt font-mono">Won</div>
              <div className="font-heading text-xl text-volt">£{wonTotal.toLocaleString()}</div>
            </div>
          </div>
          <div className="border border-line px-4 py-3 flex items-center gap-3" data-testid="kpi-in-flight">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">In flight</div>
              <div className="font-heading text-xl text-white">{inFlight}</div>
            </div>
          </div>
          {atRisk.length > 0 && (
            <div className="border border-[#FF6B6B]/60 bg-[#FF6B6B]/5 px-4 py-3 flex items-center gap-3" data-testid="kpi-at-risk" title="Negotiating > 14 days without an update">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FF6B6B] font-mono">At risk · 14d+</div>
                <div className="font-heading text-xl text-[#FF6B6B]">{atRisk.length}</div>
              </div>
            </div>
          )}
        </div>

        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-3 overflow-x-auto" data-testid="kanban-board">
            {COLS.map(col => (
              <KanbanColumn
                key={col.key}
                col={col}
                items={deals.filter(d => d.status === col.key)}
                onEdit={setEditing}
              />
            ))}
          </div>
          <DragOverlay>
            {activeDeal ? <DealCardPreview deal={activeDeal} /> : null}
          </DragOverlay>
        </DndContext>

        <CommercialNotice />
      </div>

      {editing && <DealModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </DashboardLayout>
  );
}

function DealModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bg-surface border border-line w-full max-w-2xl" onClick={(e)=>e.stopPropagation()} data-testid="deal-modal">
        <div className="px-6 py-4 border-b border-line flex justify-between items-center">
          <div>
            <div className="label-eyebrow">Edit deal</div>
            <h3 className="font-heading text-2xl uppercase">{form.brand?.brand_name || "Untitled"}</h3>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <F label="Status">
            <select value={form.status} onChange={(e)=>upd("status", e.target.value)} data-testid="d-status" className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white">
              {COLS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </F>
          <F label="Estimated value (£)"><I type="number" value={form.estimated_value} onChange={(v)=>upd("estimated_value", v)} testid="d-value" /></F>
          <F label="Next follow-up (YYYY-MM-DD)"><I value={form.next_follow_up_date} onChange={(v)=>upd("next_follow_up_date", v)} testid="d-followup" /></F>
          <F label="Notes" wide><T rows={3} value={form.notes} onChange={(v)=>upd("notes", v)} testid="d-notes" /></F>
        </div>
        <div className="px-6 py-4 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost-line">Cancel</button>
          <button onClick={()=>onSave(form)} className="btn-volt" data-testid="d-save">Save</button>
        </div>
      </div>
    </div>
  );
}
function F({ label, children, wide }) { return <div className={wide ? "md:col-span-2" : ""}><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
function I({ value, onChange, type = "text", testid }) { return <input type={type} value={value ?? ""} onChange={(e)=>onChange(e.target.value)} data-testid={testid} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white" />; }
function T({ value, onChange, rows = 3, testid }) { return <textarea rows={rows} value={value ?? ""} onChange={(e)=>onChange(e.target.value)} data-testid={testid} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white resize-none" />; }
